import React, { useState, useEffect } from 'react';
import Popup from 'devextreme-react/popup';
import { UserOutlined, CalendarOutlined, ClockCircleOutlined, IdcardOutlined } from '@ant-design/icons';
import { formatEstimateHours } from 'utils/EstimatedHourFormat';
import { ExtendedTicketInformation, Role, Status, TitleDetail } from 'components/Mytask/mtDetails/mtDetails-Content/WorkTicket/interface';
import { ButtonItem, Form, Item } from 'devextreme-react/form';
import { findAllRole, findAllStatus, findAllTitleDetail, updateTicket } from 'components/Mytask/mtDetails/mtDetails-Content/WorkTicket/api';
const tam =1;
const CardTicket: React.FC<{ ticket: ExtendedTicketInformation }> = ({ ticket }) => {
    const [popupVisible, setPopupVisible] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [formData, setFormData] = useState<ExtendedTicketInformation>({ ...ticket });
    const [roles, setRoles] = useState<Role[]>([]);
    const [statuses, setStatuses] = useState<Status[]>([]);
    const [titleDetails, setTitleDetails] = useState<TitleDetail[]>([]);



    useEffect(() => {
        const fetchData = async () => {
            try {
                const [allRoles, allStatuses, allTitleDetails] = await Promise.all([
                    findAllRole(),
                    findAllStatus(),
                    findAllTitleDetail(),
                ]);
                setRoles(allRoles);
                setStatuses(allStatuses);
                setTitleDetails(allTitleDetails);
            } catch (error) {
                console.error('Failed to fetch form data:', error);
            }
        };
        fetchData();
    }, []);

    const handleMouseDown = () => setIsDragging(false);
    const handleMouseMove = () => setIsDragging(true);
    const handleClick = () => {
        if (!isDragging) {
            setPopupVisible(true);
        }
    };

    const handleSave = async () => {
        try {
            await updateTicket(ticket.id, formData);
            setPopupVisible(false);
        } catch (error) {
            console.error('Failed to update ticket:', error);
        }
    };

    const isVisible = ticket.statusId === 1;
    const endDate = ticket.endDate ? new Date(ticket.endDate) : null;
    const startDate = ticket.startDate ? new Date(ticket.startDate) : null;
    const days = endDate && startDate
        ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    const status =
        ticket.estimateHours >= 100
            ? 'On time'
            : days < 0
                ? `Late ${Math.abs(days)} day${Math.abs(days) > 1 ? 's' : ''}`
                : `${days} day${days > 1 ? 's' : ''} left`;

    return (
        <>
            <div
                className="card"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
            >
                {!isVisible && (
                    <div className={`status-label ${days < 0 ? 'late' : 'on-time'}`}>
                        <ClockCircleOutlined />
                        <span>{status}</span>
                    </div>
                )}
                <div className="task-system">{ticket.taskId}</div>
                <div className="task-subject">{ticket.titleDetailName}</div>
                <div className="task-meta">
                    <span>
                        <UserOutlined /> {ticket.user?.nickname || 'Unknown User'}
                    </span>
                    <span>
                        <IdcardOutlined /> {ticket.roleName}
                    </span>
                    <span>
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        {formatEstimateHours(ticket.estimateHours)}
                    </span>
                </div>
            </div>

            <Popup
                visible={popupVisible}
                onHiding={() => setPopupVisible(false)}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showTitle={true}
                title={`Ticket #${ticket.taskId}`}
                width={600}
                height="auto"
            >
                <Form formData={formData} labelLocation="top" colCount={2}>
                    <Item
                        dataField="userId"
                        editorType="dxSelectBox"
                        editorOptions={{
                            items: [{ id: ticket.userId, name: ticket.user?.nickname || 'Unknown User' }],
                            valueExpr: 'id',
                            displayExpr: 'name',
                            readOnly: true,
                        }}
                        label={{ text: 'User' }}
                        disabled={true}
                    />

                    <Item
                        dataField="roleId"
                        editorType="dxSelectBox"
                        editorOptions={{
                            items: roles,
                            valueExpr: 'id',
                            displayExpr: 'roleName',
                            placeholder: 'Select Role',
                        }}
                        label={{ text: 'Role' }}
                        colSpan={2}
                        disabled={true}
                    />

                    <Item
                        dataField="titleDetailId"
                        editorType="dxSelectBox"
                        editorOptions={{
                            items: titleDetails,
                            valueExpr: 'id',
                            displayExpr: 'titleDetailName',
                            placeholder: 'Select Title Detail',
                        }}
                        label={{ text: 'Title' }}
                        disabled={true}
                    />

                    <Item
                        dataField="startDate"
                        editorType="dxDateBox"
                        editorOptions={{
                            displayFormat: 'yyyy-MM-dd',
                            useMaskBehavior: true,
                            value: formData.startDate ? new Date(formData.startDate) : null,
                        }}
                        label={{ text: 'Start Date' }}
                        colSpan={2}
                        disabled={true}
                    />

                    <Item
                        dataField="estimateHours"
                        editorType="dxNumberBox"
                        editorOptions={{
                            placeholder: 'Hours',
                            min: 0,
                            showSpinButtons: true,
                        }}
                        label={{ text: 'Estimate Hours' }}
                        disabled={true}
                    />
                    <Item
                        dataField="endDate"
                        editorType="dxDateBox"
                        editorOptions={{
                            displayFormat: 'yyyy-MM-dd',
                            useMaskBehavior: true,
                            value: formData.endDate ? new Date(formData.endDate) : null,
                        }}
                        label={{ text: 'End Date' }}
                        colSpan={2}
                        disabled={true}
                    />
                    <Item
                        dataField="statusId"
                        editorType="dxSelectBox"
                        editorOptions={{
                            items: statuses,
                            valueExpr: 'id',
                            displayExpr: 'statusName',
                            placeholder: 'Select Status',
                        }}
                        label={{ text: 'Status' }}
                        disabled={true}
                    />
                    <Item
                        dataField="completedDate"
                        editorType="dxDateBox"
                        editorOptions={{
                            displayFormat: 'yyyy-MM-dd',
                            useMaskBehavior: true,
                            value: formData.completedDate ? new Date(formData.completedDate) : null,
                        }}
                        label={{ text: 'Completed Date' }}
                        colSpan={2}
                        disabled={true}
                    />

                    <ButtonItem
                        horizontalAlignment="right"
                        buttonOptions={{
                            text: 'Save',
                            type: 'success',
                            onClick: handleSave,
                        }}
                        colSpan={2}
                    />
                </Form>
            </Popup>
        </>
    );
};

export default CardTicket;
